// Anchored popover for "Registrar mantención" (ADR-0083) — replaces the
// old instant-fire, no-confirmation icon button. A maintenance-item
// service mark creates a permanent append-only audit row
// (maintenance_item_events), so it gets the same confirmation weight a
// permanent record deserves, not a single click.
//
// Positioning engine ported from DelegationStatusControl.tsx (portal to
// document.body, getBoundingClientRect anchoring, reposition on resize,
// close on outside-click/Escape) — but reposition-on-scroll instead of
// close-on-scroll: DelegationStatusControl's popup is a stateless
// listbox with nothing to lose on close, this one holds typed date/note
// state, and closing on an incidental page scroll would silently
// discard it.
//
// Focus management is deliberately NOT ported from
// DelegationStatusControl or DelegationNoteDialog — both predate
// useDialogFocus and still have no Tab trap or focus restore (a real,
// currently-open gap, unlike AssignDrawer/ChecklistTemplateAssignDrawer
// which have since adopted the hook). Built on useDialogFocus instead,
// matching that conformant shape.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMarkMaintenanceItemServiced } from "../lib/api/queries";
import { ApiError } from "../lib/api/error";
import { useDialogFocus } from "../lib/useDialogFocus";

function todayISO(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

const POPOVER_WIDTH = 300;

export function MaintenanceServicePopover({
  assetId,
  itemId,
  itemLabel,
  anchorEl,
  onClose,
  onServiced,
}: {
  assetId: string;
  itemId: string;
  itemLabel: string;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  /** Fired right before the popover closes on a successful mark — lets
   *  the parent show its own toast (same WCAG 4.1.3 rationale as
   *  AssignDrawer's onSent / DetalleTienda's own parent-owned toast). */
  onServiced: (label: string) => void;
}) {
  const today = todayISO();
  const [date, setDate] = useState(today);
  const [note, setNote] = useState("");
  const [touched, setTouched] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const mutation = useMarkMaintenanceItemServiced();

  useDialogFocus(true, containerRef);

  const reposition = useCallback(() => {
    if (anchorEl) setRect(anchorEl.getBoundingClientRect());
  }, [anchorEl]);

  useLayoutEffect(() => {
    reposition();
  }, [reposition]);

  useEffect(() => {
    const onScroll = () => reposition();
    const onResize = () => reposition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [reposition]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    function onPointer(e: MouseEvent) {
      const t = e.target as Node;
      if (containerRef.current?.contains(t)) return;
      if (anchorEl?.contains(t)) return;
      onClose();
    }
    document.addEventListener("mousedown", onPointer);
    return () => document.removeEventListener("mousedown", onPointer);
  }, [onClose, anchorEl]);

  const isFuture = date > today;

  async function handleConfirm() {
    setTouched(true);
    if (isFuture) return;
    try {
      // Noon local time on the selected date — avoids the day-boundary
      // ambiguity a bare midnight-UTC timestamp would risk for Chilean
      // timezones (UTC-3/-4): "today" selected near local midnight could
      // otherwise serialize to a UTC instant the server reads as
      // tomorrow, tripping the not-future bound for no real reason.
      const servicedAt = new Date(`${date}T12:00:00`).toISOString();
      const trimmedNote = note.trim();
      await mutation.mutateAsync({
        assetId,
        itemId,
        servicedAt,
        ...(trimmedNote ? { note: trimmedNote } : {}),
      });
      onServiced(itemLabel);
    } catch {
      // mutation.error is surfaced inline below; popover stays open with
      // the entered date/note preserved so the user can retry without
      // re-typing (mirrors AssignDrawer's error-preservation posture).
    }
  }

  if (!rect) return null;

  const apiErrMessage =
    mutation.error instanceof ApiError
      ? mutation.error.message
      : mutation.error
        ? "No pudimos registrar la mantención. Intenta de nuevo."
        : null;

  const dateErrorId = "maint-service-date-error";
  const dateHintId = "maint-service-date-hint";
  const titleId = "maint-service-popover-title";

  return createPortal(
    <div
      ref={containerRef}
      className="maint-service-popover"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      style={{
        top: rect.bottom + 4,
        left: Math.max(8, rect.right - POPOVER_WIDTH),
        width: POPOVER_WIDTH,
      }}
    >
      <div id={titleId} className="maint-service-popover__title">
        Registrar mantención · {itemLabel}
      </div>

      <div className="field">
        <label className="field__label" htmlFor="maint-service-date">
          Fecha de la mantención
        </label>
        <input
          id="maint-service-date"
          type="date"
          className="input"
          max={today}
          value={date}
          onBlur={() => setTouched(true)}
          onChange={(e) => setDate(e.target.value)}
          aria-describedby={touched && isFuture ? dateErrorId : dateHintId}
          aria-invalid={touched && isFuture ? true : undefined}
        />
        <p id={dateHintId} className="cell-sub">
          No puede ser una fecha futura.
        </p>
        {touched && isFuture && (
          <p id={dateErrorId} className="auth-error" role="alert">
            La fecha no puede ser posterior a hoy.
          </p>
        )}
      </div>

      <div className="field">
        <label className="field__label" htmlFor="maint-service-note">
          Nota (opcional)
        </label>
        <textarea
          id="maint-service-note"
          className="textarea"
          rows={2}
          maxLength={500}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Ej.: se revisó el extintor de electricidad, faltó el de ropa."
        />
        {/* Purpose-limitation + peer-visibility disclosure (ADR-0086
            Decision 10) — this note became readable for the first time
            with the history endpoint, to an audience that now includes
            asset-scoped roles, not just admin/company_manager. */}
        <p className="field__hint">
          Describe el trabajo realizado — no incluyas nombres de terceros ni datos personales.
          Cualquiera con acceso a esta tienda puede leer esta nota.
        </p>
      </div>

      {apiErrMessage && (
        <p className="auth-error" role="alert">
          {apiErrMessage}
        </p>
      )}

      <div className="maint-service-popover__foot">
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={onClose}
          disabled={mutation.isPending}
        >
          Cancelar
        </button>
        <button
          type="button"
          className="btn btn--primary btn--sm"
          disabled={mutation.isPending}
          onClick={() => void handleConfirm()}
        >
          {mutation.isPending ? "Registrando…" : "Registrar"}
        </button>
      </div>
    </div>,
    document.body
  );
}
